import type { IntlShape } from 'react-intl';
import type { SeriesDto } from '~/backend/connector-next-api/connector-next-schemas';
import { toRecordedDatasetOrigin } from '~/domain/adapters/toRecordedDatasetOrigin';
import { provenanceRows } from '~/components/Provenance/provenanceRows';
import {
    datasetSyncStatusMeta,
    roiDisplayColorCss,
    seriesKind,
    seriesModality,
    sliceGeometry,
    wireLookup,
    type DatasetKind,
} from './patientListModel';
import type { CatalogMessageId } from '~/i18n';
import { parseWireDate } from '~/utils/date';
import { formatDateTime } from '~/utils/intlDateTime';
import type { DistanceScale } from '~/utils/units';

/** Review/approval state of a structure set or registration (connector StructureSetApprovalStatus). */
const APPROVAL_META = {
    Unapproved: { labelId: 'unapproved', tone: 'unapproved' },
    Approved: { labelId: 'approved', tone: 'approved' },
    Rejected: { labelId: 'rejected', tone: 'rejected' },
} as const satisfies Record<
    string,
    { labelId: CatalogMessageId; tone: ApprovalTone }
>;

export type ApprovalTone = 'unapproved' | 'approved' | 'rejected';

/** How a structure set came into existence (connector StructureSetOrigin). */
const ORIGIN_LABEL_IDS = {
    Unknown: 'patientList.dataset.origin.unknown',
    TpsManual: 'patientList.dataset.origin.tpsManual',
    TpsAutomatic: 'patientList.dataset.origin.tpsAutomatic',
    MvisionContourPlus: 'patientList.dataset.origin.mvisionContourPlus',
    MvisionAdaptPlus: 'patientList.dataset.origin.mvisionAdaptPlus',
    ThirdPartyAi: 'patientList.dataset.origin.thirdPartyAi',
    Imported: 'patientList.dataset.origin.imported',
} as const satisfies Record<string, CatalogMessageId>;

/** One label/value line of the dataset details. */
export type DatasetInfoRow =
    | {
          labelId: CatalogMessageId;
          value: string;
          /**
           * `sensitive` is patient-identifying text (a dataset's own name) and
           * `uid` a DICOM identifier; both go through the hide-sensitive-info
           * mechanism, the identifier shortened.
           */
          kind: 'text' | 'sensitive' | 'uid';
      }
    | {
          labelId: CatalogMessageId;
          /** A distance in domain units; the UI converts and labels it at its scale's precision. */
          kind: 'lengthMm';
          valueMm: number;
          scale: DistanceScale;
      };

export type DatasetApproval = {
    label: string;
    tone: ApprovalTone;
    /** Who set the approval state, when the connector recorded it. */
    reviewer: string | undefined;
};

export type DatasetStructures = {
    /** The structures named first, in ROI order, each with its display colour when parseable. */
    shown: Array<{ name: string; color: string | undefined }>;
    /** How many structures follow the shown ones. */
    hidden: number;
    total: number;
};

export type DatasetInfo = {
    kind: DatasetKind;
    modality: string;
    name: string;
    approval: DatasetApproval | undefined;
    rows: DatasetInfoRow[];
    structures: DatasetStructures | undefined;
};

/** Structures listed by name in the details before the rest collapse into a "+N more". */
const STRUCTURES_SHOWN = 6;

/** Display name of a series: its description, else its wire type, else the generic "Series". */
export function seriesDisplayName(intl: IntlShape, series: SeriesDto): string {
    return (
        series.seriesDescription ||
        series.datasetType ||
        intl.formatMessage({ id: 'series' })
    );
}

/**
 * Wire timestamp as a localized day and clock time ("Feb 18, 2026, 09:00"),
 * or '' when missing or unparseable.
 */
function formatDateTimeText(
    intl: IntlShape,
    value: string | null | undefined
): string {
    const parsed = parseWireDate(value);
    return parsed ? formatDateTime(intl, parsed) : '';
}

function approvalOf(
    intl: IntlShape,
    status: string | null | undefined,
    reviewer: string | null | undefined
): DatasetApproval | undefined {
    const meta = wireLookup(APPROVAL_META, status);
    return (
        meta && {
            label: intl.formatMessage({ id: meta.labelId }),
            tone: meta.tone,
            reviewer: reviewer || undefined,
        }
    );
}

function originLabel(
    intl: IntlShape,
    method: string | null | undefined
): string | undefined {
    const labelId = wireLookup(ORIGIN_LABEL_IDS, method);
    return labelId && intl.formatMessage({ id: labelId });
}

/**
 * The display name of the sibling dataset a DICOM UID references, or the UID
 * itself when the patient holds no such dataset, so a dangling reference is
 * still shown rather than silently dropped. An image is referenced by its
 * series UID; a structure set or plan by the SOP instance UID of its one
 * object. Both are globally unique, so one lookup serves both.
 */
function referencedSeriesText(
    intl: IntlShape,
    uid: string,
    siblings: readonly SeriesDto[]
): { value: string; kind: 'sensitive' | 'uid' } {
    const target = siblings.find(
        (series) =>
            series.seriesInstanceUid === uid || series.sopInstanceUid === uid
    );
    return target
        ? { value: seriesDisplayName(intl, target), kind: 'sensitive' }
        : { value: uid, kind: 'uid' };
}

/** "N" received slices, or "N of M" while the expected count is known and differs. */
function sliceCountText(intl: IntlShape, series: SeriesDto): string {
    const received = series.slices?.length ?? 0;
    const expected = series.expectedSliceCount ?? undefined;
    return expected !== undefined && expected !== received
        ? intl.formatMessage(
              { id: 'patientList.dataset.info.slicesOfExpected' },
              { received, expected }
          )
        : String(received);
}

/**
 * The facts the connector records about one dataset, arranged for display:
 * only present values are listed, dates are localized, and identifiers are
 * marked so the UI can render them as identifiers. Structure sets add their
 * approval state and the first few structure names; registrations their
 * approval state and both frames.
 */
export function datasetInfo(
    intl: IntlShape,
    series: SeriesDto,
    siblings: readonly SeriesDto[]
): DatasetInfo {
    const kind = seriesKind(series);
    const rows: DatasetInfoRow[] = [];
    const add = (
        labelId: CatalogMessageId,
        value: string | null | undefined,
        rowKind: 'text' | 'uid' = 'text'
    ) => {
        if (value) {
            rows.push({ labelId, value, kind: rowKind });
        }
    };
    const addReference = (
        labelId: CatalogMessageId,
        uid: string | null | undefined
    ) => {
        if (uid) {
            const reference = referencedSeriesText(intl, uid, siblings);
            rows.push({
                labelId,
                value: reference.value,
                kind: reference.kind,
            });
        }
    };

    add(
        kind === 'image'
            ? 'patientList.dataset.info.acquired'
            : 'patientList.dataset.info.created',
        formatDateTimeText(intl, series.seriesDateTime)
    );

    if (kind === 'image') {
        add('patientList.dataset.info.slices', sliceCountText(intl, series));
        const geometry = sliceGeometry(series);
        if (geometry?.spacingMm !== undefined) {
            rows.push({
                labelId: 'patientList.dataset.info.sliceSpacing',
                kind: 'lengthMm',
                valueMm: geometry.spacingMm,
                scale: 'voxel',
            });
        } else if (geometry) {
            add(
                'patientList.dataset.info.sliceSpacing',
                intl.formatMessage({
                    id: 'patientList.dataset.info.unevenSpacing',
                })
            );
        }
        if (geometry) {
            rows.push({
                labelId: 'patientList.dataset.info.extent',
                kind: 'lengthMm',
                valueMm: geometry.extentMm,
                scale: 'macro',
            });
        }
        if (series.isValid3D === false) {
            add(
                'patientList.dataset.info.volume',
                intl.formatMessage({
                    id: 'patientList.dataset.info.notValid3D',
                })
            );
        }
    }
    if (kind === 'structureSet') {
        addReference(
            'patientList.dataset.info.referencedImage',
            series.structureSet?.referencedImageSeriesUid
        );
        const recorded = toRecordedDatasetOrigin(series.structureSet?.origin);
        const legacyMethod = series.structureSet?.generationMethod;
        if (!recorded && legacyMethod && legacyMethod !== 'Unknown') {
            // A set stored before origins were recorded, whose generation
            // was stated afterwards: show that statement as it stands.
            add(
                'patientList.dataset.info.origin',
                originLabel(intl, legacyMethod)
            );
            add(
                'patientList.dataset.info.model',
                [
                    series.structureSet?.generationModelId,
                    series.structureSet?.generationModelVersion,
                ]
                    .filter(Boolean)
                    .join(' · ')
            );
        } else {
            for (const row of provenanceRows(intl, {
                recorded,
                structures: (series.structureSet?.structures ?? []).map(
                    (structure) => ({
                        modelGenerated: structure.modelGenerated ?? false,
                        contourEditedSinceGeneration:
                            structure.contourEditedSinceGeneration ?? false,
                    })
                ),
            })) {
                add(row.labelId, row.value);
            }
        }
    }
    if (kind === 'plan') {
        addReference(
            'patientList.dataset.info.referencedStructureSet',
            series.referencedStructureSetUid
        );
    }
    if (kind === 'dose') {
        addReference(
            'patientList.dataset.info.referencedPlan',
            series.referencedRtPlanUid
        );
        addReference(
            'patientList.dataset.info.referencedStructureSet',
            series.referencedStructureSetUid
        );
    }
    if (kind === 'registration') {
        add(
            'patientList.dataset.info.sourceFrame',
            series.sourceFrameOfReferenceUid,
            'uid'
        );
        add(
            'patientList.dataset.info.targetFrame',
            series.targetFrameOfReferenceUid,
            'uid'
        );
    }
    add(
        'patientList.dataset.info.retrieval',
        series.isReceiving === true
            ? intl.formatMessage({
                  id: 'patientList.dataset.info.stillReceiving',
              })
            : datasetSyncStatusMeta(intl, series.status)?.label
    );
    add('patientList.dataset.info.seriesUid', series.seriesInstanceUid, 'uid');
    if (kind !== 'registration') {
        add(
            'patientList.dataset.info.frameOfReference',
            series.frameOfReferenceUid,
            'uid'
        );
    }

    let approval: DatasetApproval | undefined;
    if (kind === 'structureSet') {
        approval = approvalOf(
            intl,
            series.structureSet?.approvalStatus,
            series.structureSet?.reviewerName
        );
    } else if (kind === 'registration') {
        approval = approvalOf(
            intl,
            series.registration?.approvalStatus,
            series.registration?.reviewerName
        );
    }

    let structures: DatasetStructures | undefined;
    if (kind === 'structureSet') {
        const named = (series.structureSet?.structures ?? []).filter(
            (structure) => !!structure.roiName
        );
        const total = series.structureSet?.structureCount ?? named.length;
        if (total > 0) {
            const shown = named.slice(0, STRUCTURES_SHOWN).map((structure) => ({
                name: structure.roiName ?? '',
                color: roiDisplayColorCss(structure.color),
            }));
            structures = {
                shown,
                hidden: Math.max(0, total - shown.length),
                total,
            };
        }
    }

    return {
        kind,
        modality: seriesModality(series),
        name: seriesDisplayName(intl, series),
        approval,
        rows,
        structures,
    };
}
